// Position selector for a Multi-Position card.
//
// The row is the ANSWER to "where can this guy play" - always visible, one chip
// per carded position, each carrying that position's Range/Glove so the
// comparison is legible without clicking anything (Strat prints the same list at
// the top of a card; OOTP shows one Position Rating per position). Selecting a
// chip re-renders the ratings table AT that position.
//
// Not rendered for a single-position card: a lone chip repeats the position
// already on the page and offers nothing to switch to.

import type { PlayerPositionRating } from '@/api/generated/models/playerPositionRating'
import { cn } from '@/lib/utils'
import { ratingColorForValue } from '@/features/_shared/ratings'
import { archetypePositionLabel, POSITION_REDUCTION_NOTE } from '@/features/_shared/archetypePosition'

export function PositionChips({
  rows,
  selectedDposn,
  onSelect,
}: {
  rows: PlayerPositionRating[]
  selectedDposn?: number
  onSelect: (dposn: number) => void
}) {
  if (rows.length < 2) return null

  return (
    <div className="space-y-1">
    <div className="flex flex-wrap items-end gap-2">
      {/* Reads as an instruction rather than a heading: the chips look like a stat
          line, so without a verb nothing tells you they can be clicked. */}
      <span className="pb-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">Ratings at</span>
      <div className="inline-flex flex-wrap rounded-lg border border-slate-300 bg-slate-100 p-0.5" role="group" aria-label="Position">
        {rows.map((r) => {
          const active = r.dposn === selectedDposn
          return (
            <div key={r.dposn} className="flex flex-col items-center">
              {/* Caption rides ABOVE the chip rather than inside it: inside, it
                  competed with the ratings for the same line. Rendered empty on
                  the others so every button still sits on one baseline. */}
              <span className="h-3.5 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                {r.isPrimary ? 'Primary' : ''}
              </span>
            <button
              type="button"
              aria-pressed={active}
              onClick={() => r.dposn != null && onSelect(r.dposn)}
              className={cn(
                'flex items-baseline gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                // Selected fills navy rather than the usual white-pill segmented
                // control: this one changes the numbers below it, so it has to
                // read as loud as the change it causes.
                // Selection is a navy RING, not a navy fill: filled, the rating
                // colours below would be unreadable, and those colours are the
                // point - a red rating dropping to black at another position is
                // the comparison this row exists to make.
                'border bg-white shadow-sm',
                active
                  ? 'border-navy ring-2 ring-inset ring-navy'
                  : 'border-slate-300 hover:border-navy/40 hover:bg-slate-50',
              )}
            >
              {/* No "@" prefix: the row is headed "Ratings at", so the chip
                  saying "@SS" repeats the preposition the label already supplied. */}
              <span className={cn('font-heading font-bold', active ? 'text-navy' : 'text-slate-600')}>
                {archetypePositionLabel(r.abbr)}
              </span>
              {/* Range and Glove at that position - the two ratings the archetype
                  actually reduces. The fielding overall would summarise them, but
                  it hides which of the two the move costs him, which is the whole
                  question when picking where to play someone. Each chip carries its
                  own units so the row needs no legend and survives wrapping.
                  Numbers use the site rating scale, so a red rating fading to black
                  across positions is visible without reading the digits. */}
              <span className="tabular-nums">
                <span className={ratingColorForValue(r.ra?.current)}>{r.ra?.current ?? '—'}</span>
                <span className="text-[10px] font-semibold text-slate-400">RA</span>
                {/* NOT a slash: the position label may itself contain one
                    ("LF/RF"), and two slashes side by side read as one series. */}
                <span className="px-1 text-slate-300">·</span>
                <span className={ratingColorForValue(r.gl?.current)}>{r.gl?.current ?? '—'}</span>
                <span className="text-[10px] font-semibold text-slate-400">GL</span>
              </span>
            </button>
            </div>
          )
        })}
      </div>
    </div>
    {/* The list is meaningless without this: it reads as "these are his
        positions" when what it means is "these are the positions that do not
        wreck him". Nothing stops an owner playing him anywhere. */}
    <p className="text-xs text-slate-500">{POSITION_REDUCTION_NOTE}</p>
    </div>
  )
}